export type SeasonTemplateBetType = "moneyline" | "spread" | "total" | "total_tiebreaker" | "custom_prop";

export interface SeasonTemplateWeekReference {
  weekId: string;
  roundKey: string;
  roundLabel: string;
  order: number;
}

export interface SeasonTemplateBet {
  id: string;
  nflGameId: string;
  nflBetType: SeasonTemplateBetType;
  oddsOne: number | null;
  oddsTwo: number | null;
  displayInfo?: unknown;
}

export interface SeasonTemplateGame {
  id: string;
  weekId: string;
  homeTeamId: string;
  awayTeamId: string;
  gameTime: string | Date;
  network: string | null;
}

export interface SeasonTemplateWeek {
  reference: SeasonTemplateWeekReference;
  week: {
    id: string;
    season: number;
    week: number;
    startDate: string | Date;
    endDate: string | Date;
    isActive?: boolean | null;
  };
  games: {
    game: SeasonTemplateGame;
    bets: SeasonTemplateBet[];
  }[];
}

export interface SeasonTemplateSummary {
  id: string;
  label: string;
  description: string;
  seasonIdentifier: string;
  seasonYear: number;
  weekCount: number;
  gameCount: number;
  betCount: number;
  availableBetTypes: SeasonTemplateBetType[];
  metadata?: Record<string, unknown>;
}

export interface SeasonTemplateDetail extends SeasonTemplateSummary {
  weeks: SeasonTemplateWeek[];
}

export const getLastGameIdInTemplateWeek = (templateWeek: SeasonTemplateWeek): string | null => {
  let lastGameId: string | null = null;
  let lastGameTime = Number.NEGATIVE_INFINITY;

  for (const entry of templateWeek.games) {
    const gameTime = new Date(entry.game.gameTime).getTime();
    const comparableGameTime = Number.isFinite(gameTime) ? gameTime : Number.NEGATIVE_INFINITY;
    if (lastGameId === null || comparableGameTime >= lastGameTime) {
      lastGameId = entry.game.id;
      lastGameTime = comparableGameTime;
    }
  }

  return lastGameId;
};

type TiebreakerToggleBetState = {
  nflBetId: string;
  enabled: boolean;
};

type TiebreakerToggleWeekState<TBet extends TiebreakerToggleBetState> = {
  weekId: string;
  enabled: boolean;
  bets: TBet[];
};

export const applyTotalTiebreakerToggle = <TBet extends TiebreakerToggleBetState>(
  weekStates: TiebreakerToggleWeekState<TBet>[],
  templateDetail: SeasonTemplateDetail,
  lockedBetIds: ReadonlySet<string>,
  enabled: boolean,
): TiebreakerToggleWeekState<TBet>[] => {
  const totalTiebreakerIds = new Set<string>();
  const lastGameTiebreakerIdsByWeek = new Map<string, Set<string>>();

  for (const templateWeek of templateDetail.weeks) {
    const lastGameId = getLastGameIdInTemplateWeek(templateWeek);
    const lastGameTiebreakerIds = new Set<string>();

    for (const gameEntry of templateWeek.games) {
      for (const bet of gameEntry.bets) {
        if (bet.nflBetType !== "total_tiebreaker") continue;
        totalTiebreakerIds.add(bet.id);
        if (gameEntry.game.id === lastGameId) {
          lastGameTiebreakerIds.add(bet.id);
        }
      }
    }

    lastGameTiebreakerIdsByWeek.set(templateWeek.week.id, lastGameTiebreakerIds);
  }

  return weekStates.map((weekState) => {
    const lastGameTiebreakerIds = lastGameTiebreakerIdsByWeek.get(weekState.weekId) ?? new Set<string>();
    return {
      ...weekState,
      bets: weekState.bets.map((bet) => {
        if (!totalTiebreakerIds.has(bet.nflBetId) || lockedBetIds.has(bet.nflBetId)) {
          return bet;
        }
        return {
          ...bet,
          enabled: enabled && lastGameTiebreakerIds.has(bet.nflBetId),
        };
      }),
    };
  });
};

export interface LeagueWeekBetScoringOverrideOddsBased {
  type: "oddsBased";
  multiplier?: number | null;
}

export interface LeagueWeekBetScoringOverrideFixedPoints {
  type: "nPoints";
  one: number;
  two: number;
  three?: number;
  four?: number;
  five?: number;
  six?: number;
  seven?: number;
  eight?: number;
  nine?: number;
  ten?: number;
  eleven?: number;
  // Legacy fields
  side1?: number;
  side2?: number;
}

export type LeagueWeekBetScoringOverride =
  | LeagueWeekBetScoringOverrideOddsBased
  | LeagueWeekBetScoringOverrideFixedPoints;

export interface SeasonTemplateBetConfig {
  nflBetId: string;
  enabled: boolean;
  scoringOverride?: LeagueWeekBetScoringOverride | null;
  sortOrder?: number | null;
}

export interface SeasonTemplateWeekConfig {
  weekId: string;
  enabled: boolean;
  propOfTheWeekNflBetId?: string | null;
  bets: SeasonTemplateBetConfig[];
}

export interface SeasonTemplateInstanceConfig {
  templateId: string;
  weeks: SeasonTemplateWeekConfig[];
}

export type PickLockMode = "gameStart" | "roundStart" | "afterFirstNGames";

export interface PickLockPolicyConfig {
  mode: PickLockMode;
  gamesCount?: number;
}

export interface LeagueSeasonConfigBet {
  nflBetId: string;
  betType: SeasonTemplateBetType;
  included: boolean;
  locked: boolean;
  oddsOne: number | null;
  oddsTwo: number | null;
  displayInfo?: unknown;
  scoringOverride?: LeagueWeekBetScoringOverride | null;
  sortOrder?: number | null;
}

export interface LeagueSeasonConfigGame {
  id: string;
  label: string;
  kickoffAt: string;
  locked: boolean;
  bets: LeagueSeasonConfigBet[];
}

export interface LeagueSeasonConfigWeek {
  weekId: string;
  leagueWeekId: string | null;
  label: string;
  roundKey?: string;
  roundLabel?: string;
  startsAt?: string | null;
  endsAt?: string | null;
  games: LeagueSeasonConfigGame[];
}

export interface LeagueSeasonConfigResponse {
  leagueId: string;
  seasonId: string;
  seasonLabel: string;
  templateId: string | null;
  templateLabel?: string | null;
  isEditable: boolean;
  lockedBetIds: string[];
  config: SeasonTemplateInstanceConfig | null;
  weeks: LeagueSeasonConfigWeek[];
}
